<script setup lang="ts">
import { ref, computed } from "vue";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs-v2";
import { Share2, Code, QrCode, Share, BookOpen } from "lucide-vue-next";
import ShareTab from "./tabs/ShareTab.vue";
import EmbedTab from "./tabs/EmbedTab.vue";
import SocialTab from "./tabs/SocialTab.vue";
import QrCodeTab from "./tabs/QrCodeTab.vue";
import CmsGuidesTab from "./tabs/CmsGuidesTab.vue";

interface Props {
    eventTypeGroup: {
        id: number;
        token: string;
        title: string;
        public_url: string;
    };
    open?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    open: undefined,
});

const emit = defineEmits<{
    (e: "update:open", value: boolean): void;
}>();

const internalOpen = ref(false);

const isOpen = computed({
    get: () => props.open ?? internalOpen.value,
    set: (value) => {
        if (props.open !== undefined) {
            emit("update:open", value);
        } else {
            internalOpen.value = value;
        }
    },
});

const activeTab = ref("share");

const bookingUrl = computed(() => {
    return props.eventTypeGroup.public_url;
});
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogTrigger as-child>
            <slot />
        </DialogTrigger>
        <DialogContent
            class="max-w-3xl flex max-h-[90vh] flex-col overflow-hidden"
        >
            <DialogHeader>
                <template #icon>
                    <Share2
                        class="text-z-primary-600"
                        stroke-width="2.25"
                        :size="20"
                    />
                </template>
                <DialogTitle>
                    {{ $t("event_types.share_hub.title") }}
                </DialogTitle>
                <DialogDescription>
                    {{ $t("event_types.share_hub.description") }}
                </DialogDescription>
            </DialogHeader>

            <div class="px-6 pb-6 pt-4 flex flex-1 flex-col overflow-hidden">
                <Tabs
                    v-model="activeTab"
                    class="flex flex-1 flex-col overflow-hidden"
                >
                    <TabsList
                        class="mb-6 p-1 bg-iron-100 rounded-lg inline-flex h-auto w-full"
                    >
                        <TabsTrigger
                            value="share"
                            class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                        >
                            <Share :size="16" />
                            {{ $t("event_types.share_hub.tabs.share") }}
                        </TabsTrigger>
                        <TabsTrigger
                            value="embed"
                            class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                        >
                            <Code :size="16" />
                            {{ $t("event_types.share_hub.tabs.embed") }}
                        </TabsTrigger>
                        <TabsTrigger
                            value="social"
                            class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                        >
                            <Share2 :size="16" />
                            {{ $t("event_types.share_hub.tabs.social") }}
                        </TabsTrigger>
                        <TabsTrigger
                            value="qr"
                            class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                        >
                            <QrCode :size="16" />
                            {{ $t("event_types.share_hub.tabs.qr_code") }}
                        </TabsTrigger>
                        <TabsTrigger
                            value="cms"
                            class="gap-2 rounded-md py-2 px-2 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 ring-0 ring-offset-0 data-[state=active]:ring-0"
                        >
                            <BookOpen :size="16" />
                            {{ $t("event_types.share_hub.tabs.cms") }}
                        </TabsTrigger>
                    </TabsList>

                    <div class="flex-1 overflow-y-auto">
                        <TabsContent value="share" class="mt-0">
                            <ShareTab :booking-url="bookingUrl" />
                        </TabsContent>
                        <TabsContent value="embed" class="mt-0">
                            <EmbedTab
                                :token="eventTypeGroup.token"
                                embed-type="group"
                            />
                        </TabsContent>
                        <TabsContent value="social" class="mt-0">
                            <SocialTab
                                :booking-url="bookingUrl"
                                :event-name="eventTypeGroup.title"
                            />
                        </TabsContent>
                        <TabsContent value="qr" class="mt-0">
                            <QrCodeTab :group-id="eventTypeGroup.id" />
                        </TabsContent>
                        <TabsContent value="cms" class="mt-0">
                            <CmsGuidesTab
                                :token="eventTypeGroup.token"
                                embed-type="group"
                            />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </DialogContent>
    </Dialog>
</template>
